import React, { Fragment } from "react"
import { connect } from "react-redux"
import PropTypes from "prop-types"
import { Row, Col } from "react-bootstrap"
import { Field, getFormSyncErrors, isSubmitting, isInvalid, hasSubmitFailed } from "redux-form/immutable"

import { selectFormFields } from "QuorumGrassroots/widgets/ReusableComponents/UserInfoFormSection/selectors"

import CustomFieldFormSection from "app/static/frontend/custom-fields/containers/CustomFieldFormSection"
import { phoneNumberFormatWarning } from "QuorumGrassroots/helperFunctions"

import { AddressField } from "shared-web/forms/fields/AddressField"
import { InputField } from "app/static/frontend/forms/components/InputField"
import { PhoneField } from "shared-web/forms/fields/PhoneField"
import { EmailField } from "shared-web/forms/fields/EmailField"

import ToggleField from "shared-web/forms/fields/ToggleField"
import { EmailFieldWithConfirmation } from "QuorumGrassroots/widgets/ReusableComponents/UserInfoFormSection/EmailFieldWithConfirmation"

import { StyledContainer } from "QuorumGrassroots/widgets/ReusableComponents/UserInfoFormSection/style"
import { StyledContrastText } from "QuorumGrassroots/styled-components/components/StyledContrastText"
import { AddressFieldWithOfficials } from "QuorumGrassroots/widgets/ReusableComponents/UserInfoFormSection/AddressFieldWithOfficials"
import { EmailFieldWithOfficials } from "QuorumGrassroots/widgets/ReusableComponents/UserInfoFormSection/EmailFieldWithOfficials"
import { FieldWithPlaceholderResolving } from "QuorumGrassroots/widgets/ReusableComponents/UserInfoFormSection/FieldWithPlaceholderResolving"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { CheckBoxField } from "app/static/frontend/forms/components/CheckBoxField"
import {
    getCustomValidator,
    getOptionalValidator,
} from "QuorumGrassroots/widgets/ReusableComponents/UserInfoFormSection/customValidators"
import { CheckboxField } from "shared-web/forms/fields/CheckboxField"
import ErrorFocusWrapper from "../ErrorFocusWrapper"
import { PACReadOnlyIcon } from "QuorumGrassroots/widgets/ReusableComponents/PACReadOnlyIcon"

const { CampaignType } = DjangIO.app.grassroots.campaign.types

export const UserInfoFormSection = ({ t, form, submitFailed, invalid, submitting, syncErrors, ...props }) => {
    const renderFormField = (fieldObj) => {
        const readOnlyFields = props.readOnlyFields ?? []
        const formField = DjangIO.app.grassroots.types.GrassrootsRegistrationField.by_value(
            fieldObj.registration_field_type,
        )
        const isPacActionCenter = window.action_center_settings?.is_pac_action_center
        const isSupporterRequiredData = Boolean(formField?.supporter_field)
        const supporterFieldAlloweToEdit = [DjangIO.app.grassroots.types.GrassrootsRegistrationField.text_opt_in.value]
        const isPACReadOnly =
            isPacActionCenter &&
            isSupporterRequiredData &&
            isFeatureEnabled("ff_pac_supporter_sync_enhancements") &&
            !supporterFieldAlloweToEdit.includes(formField.value)

        // First check for a custom label specified in props, otherwise default to label in i18n
        const formFieldLabel = (fieldObj.props && fieldObj.props.label) || t(formField.i18n_label_key)
        const formFieldPlaceholder = (fieldObj.props && fieldObj.props.label) || t(formField.i18n_placeholder_key)

        const optionalField = props.optionalSimpleFields[formField.value]

        // Some fields, like the texting opt-in field, can disallow the supporter
        // from making a choice; this is used for toggle fields
        const allowChoices = fieldObj.props && fieldObj.props.allow_choices

        const isRequired = !optionalField && props.requireAllFields
        const fieldType = formField.input_type

        const validation = getCustomValidator(fieldType, formFieldLabel, isRequired)

        //currently the frontend validation only checks for required fields, so if it's undefined it means the field isn't required
        const showAsterisk = !!validation

        // For phone number fields, we want to show a warning about number format and compatibility
        // with texting, but only if there is also a texting field on the form
        const warning =
            formField.input_type === DjangIO.app.grassroots.types.FormInputType.phone_number.value &&
            props.includesTextingOptIn
                ? phoneNumberFormatWarning
                : null

        const isReadOnly = readOnlyFields.includes(formField.value) || isPACReadOnly

        const hasCustomRecipients = props.campaign?._extra.message_groups.some(
            (message) => message.custom_email_targets?.length > 0,
        )
        const shouldShowOfficialsByAddress =
            isFeatureEnabled("ff_ngg_address_lookup") &&
            props.campaign?.campaign_type === CampaignType.write_member.value &&
            props.campaign?.is_one_click_registration_enabled &&
            !hasCustomRecipients

        const shouldSendEmailConfirmation = isFeatureEnabled("ff_grassroots_pii_email")

        const AddressFieldComponent = shouldShowOfficialsByAddress ? AddressFieldWithOfficials : AddressField
        const EmailFieldComponent = shouldShowOfficialsByAddress ? EmailFieldWithOfficials : EmailField
        const GenericFieldComponent = shouldShowOfficialsByAddress ? FieldWithPlaceholderResolving : Field

        // Add unique IDs for ARIA relationships
        const fieldId = `field-${formField.supporter_field}-${props.uniqueWidgetId || "main"}`
        const labelId = `${fieldId}-label`

        switch (formField.input_type) {
            case DjangIO.app.grassroots.types.FormInputType.address.value:
                return (
                    <>
                        <AddressFieldComponent
                            id={fieldId}
                            name={formField.supporter_field}
                            dataCy={formField.supporter_field}
                            label={formFieldLabel}
                            placeholder={props.allowPartialAddress ? "" : formFieldPlaceholder}
                            displayErrorWithoutTouch={false}
                            validate={validation}
                            showAsterisk={showAsterisk}
                            shouldGeocode={true}
                            accessibilityId={t(formField.i18n_label_key)}
                            isCampaignPage={props.isCampaignPage}
                            campaign={props.campaign}
                            disabled={isReadOnly}
                            IconComponent={isPACReadOnly ? PACReadOnlyIcon : null}
                            tooltipText={
                                isReadOnly ? props.tooltipText : "Select an address from the list instead of typing"
                            }
                            uniqueWidgetId={props.uniqueWidgetId}
                            // Enhanced accessibility attributes - removed problematic roles
                            aria-required={showAsterisk}
                            aria-labelledby={labelId}
                            aria-invalid={
                                props.syncErrors && props.syncErrors[formField.supporter_field] ? "true" : "false"
                            }
                        />
                    </>
                )
            case DjangIO.app.grassroots.types.FormInputType.phone_number.value:
                return (
                    <PhoneField
                        id={formField.supporter_field}
                        accessibilityId={t(formField.i18n_label_key)}
                        dataCy={formField.supporter_field}
                        displayErrorWithoutTouch={false}
                        label={formFieldLabel}
                        name={formField.supporter_field}
                        placeholder={formFieldPlaceholder}
                        validate={validation}
                        warn={warning}
                        countryCodeEditable={false}
                        defaultCountry={"us"}
                        showAsterisk={showAsterisk}
                        shouldCleanInput
                        isCampaignPage={props.isCampaignPage}
                        disabled={isReadOnly}
                        IconComponent={isPACReadOnly ? PACReadOnlyIcon : null}
                        tooltipText={isReadOnly ? props.tooltipText : null}
                        aria-required={showAsterisk}
                        aria-describedby={`${formField.supporter_field}-description`}
                        aria-invalid={
                            props.syncErrors && props.syncErrors[formField.supporter_field] ? "true" : "false"
                        }
                        aria-label={`${formFieldLabel}${showAsterisk ? ", required" : ""}`}
                        role="textbox"
                        aria-live="polite"
                        aria-atomic="true"
                    />
                )
            case DjangIO.app.grassroots.types.FormInputType.toggle.value:
                // Check if this is a text opt-in field and compliance feature flag is enabled
                const isTextOptInField =
                    formField.value === DjangIO.app.grassroots.types.GrassrootsRegistrationField.text_opt_in.value

                if (isTextOptInField) {
                    // Get organization data for dynamic compliance label
                    const orgName = props.textingOrgName || props.organizationDesign?.name || "this organization"
                    const frequency = props.textingMessageFrequency || 5
                    const termsUrlFinal = props.textingTermsUrl || "#"
                    const privacyUrlFinal = props.textingPrivacyUrl || "#"

                    const complianceLabel = t("registration.text_opt_in.sms_compliance_checkbox", {
                        orgName,
                        frequency,
                        termsUrlFinal,
                        privacyUrlFinal,
                    })

                    return (
                        <div className="toggle-field-container">
                            <CheckboxField
                                key={formField.supporter_field}
                                name={formField.supporter_field}
                                dataCy={formField.supporter_field}
                                label="" // Empty label since we'll use checkboxLabel for the compliance text
                                checkboxLabel={complianceLabel}
                                displayErrorWithoutTouch={false}
                                disabled={isReadOnly}
                                isCampaignPage={props.isCampaignPage}
                                accessibilityId={`${formField.supporter_field}-compliance`}
                                tooltipText={isReadOnly ? props.tooltipText : null}
                                // Initial value provided by form's initialValues (always false for compliance)
                                format={(value) => Boolean(value)}
                                parse={(value) => Boolean(value)}
                                // No validation for compliance checkbox (always optional)
                                validate={null}
                                showAsterisk={false}
                                // Enhanced accessibility attributes
                                aria-required={false}
                                aria-invalid={
                                    props.syncErrors && props.syncErrors[formField.supporter_field] ? "true" : "false"
                                }
                            />
                        </div>
                    )
                }

                return (
                    <div className="toggle-field-container">
                        <ToggleField
                            id={fieldId}
                            name={formField.supporter_field}
                            dataCy={formField.supporter_field}
                            label={formFieldLabel}
                            displayErrorWithoutTouch={false}
                            validate={validation}
                            showAsterisk={showAsterisk}
                            allowChoices={allowChoices}
                            isCampaignPage={props.isCampaignPage}
                            disabled={isReadOnly}
                            tooltipText={isReadOnly ? props.tooltipText : null}
                            // Enhanced accessibility attributes - proper role for checkbox
                            aria-required={showAsterisk}
                            aria-invalid={
                                props.syncErrors && props.syncErrors[formField.supporter_field] ? "true" : "false"
                            }
                            // Pass through accessible binary toggle props
                            useAccessibleBinaryToggle={props.useAccessibleBinaryToggle}
                            required={showAsterisk}
                        />
                    </div>
                )
            case DjangIO.app.grassroots.types.FormInputType.email.value:
                const emailValidation = isRequired ? validation : getOptionalValidator(formFieldLabel)

                const emailFieldProps = {
                    id: fieldId,
                    name: formField.supporter_field,
                    dataCy: formField.supporter_field,
                    label: formFieldLabel,
                    placeholder: formFieldPlaceholder,
                    displayErrorWithoutTouch: false,
                    warn: warning,
                    validate: emailValidation,
                    accessibilityId: t(formField.i18n_label_key),
                    showAsterisk: showAsterisk,
                    isCampaignPage: props.isCampaignPage,
                    campaign: props.campaign,
                    disabled: isReadOnly,
                    IconComponent: isPACReadOnly ? PACReadOnlyIcon : null,
                    shouldShowIconWithinInput: isPACReadOnly,
                    tooltipText: isReadOnly ? props.tooltipText : null,
                    uniqueWidgetId: props.uniqueWidgetId,
                    // Add accessibility attributes
                    "aria-required": showAsterisk,
                    "aria-invalid": props.syncErrors && props.syncErrors[formField.supporter_field] ? "true" : "false",
                    "aria-label": `${formFieldLabel}${showAsterisk ? ", required" : ""}`,
                    role: "textbox",
                    "aria-autocomplete": "email",
                    type: "email",
                    inputMode: "email",
                }

                if (shouldSendEmailConfirmation) {
                    return (
                        <Fragment>
                            <EmailFieldWithConfirmation
                                {...emailFieldProps}
                                organizationDesign={props.organizationDesign}
                                component={EmailFieldComponent}
                            />
                        </Fragment>
                    )
                } else {
                    return (
                        <Fragment>
                            <EmailFieldComponent {...emailFieldProps} />
                        </Fragment>
                    )
                }
            default:
                const defaultValidation = isRequired ? validation : getOptionalValidator(formFieldLabel)

                return (
                    <GenericFieldComponent
                        id={formField.supporter_field}
                        name={formField.supporter_field}
                        dataCy={formField.supporter_field}
                        label={formFieldLabel}
                        component={InputField}
                        placeholder={formFieldPlaceholder}
                        displayErrorWithoutTouch={false}
                        warn={warning}
                        validate={defaultValidation}
                        accessibilityId={t(formField.i18n_label_key)}
                        IconComponent={isPACReadOnly ? PACReadOnlyIcon : null}
                        shouldShowIconWithinInput={isPACReadOnly}
                        showAsterisk={showAsterisk}
                        isCampaignPage={props.isCampaignPage}
                        campaign={props.campaign}
                        disabled={isReadOnly}
                        tooltipText={isReadOnly ? props.tooltipText : null}
                        aria-required={!optionalField && props.requireAllFields}
                    />
                )
        }
    }

    const renderAllFieldObjects = () => {
        const allFields = []

        let doubleSimpleField = []
        if (props.allSimpleAndCustomFieldsWidth) {
            props.allSimpleAndCustomFieldsWidth.forEach((fieldObj) => {
                const slug = Object.keys(fieldObj.field)[0]
                const mappedFields = props.customFields.find((customField) => customField.slug === slug)
                const isReadOnlyCustomFields = props.readOnlyFields

                if (fieldObj.field && !fieldObj.isSimple) {
                    allFields.push(
                        <CustomFieldFormSection
                            allFieldsMandatory={props.requireAllFields}
                            customFieldNameDict={fieldObj.field}
                            externalFacing
                            form={props.form}
                            optionalFields={props.customFieldOptionalFields}
                            isCampaignPage={props.isCampaignPage}
                            requiredValues={props.customFieldRequiredValues}
                            selectLabel={t("form.select_placeholder")}
                            toggleChoices={[
                                { label: t("form.toggle_true"), value: true },
                                { label: t("form.toggle_false"), value: false },
                            ]}
                            disabled={
                                props.readOnlyFields && mappedFields && props.readOnlyFields.includes(mappedFields.id)
                            }
                            toolTipTextActionCenterSettings={
                                props.readOnlyFields && mappedFields && props.readOnlyFields.includes(mappedFields.id)
                                    ? props.tooltipText
                                    : null
                            }
                            isReadOnlyCustomFields={isReadOnlyCustomFields}
                            useAccessibleBinaryToggle={props.useAccessibleBinaryToggle}
                        />,
                    )
                }

                if (fieldObj.field && fieldObj.isSimple) {
                    if (fieldObj.md === 12) {
                        allFields.push(
                            <Row>
                                <Col key={fieldObj.field.order} md={fieldObj.md} sm={12}>
                                    {renderFormField(fieldObj.field)}
                                </Col>
                            </Row>,
                        )
                    } else if (fieldObj.md === 6) {
                        doubleSimpleField.push(
                            <Col key={fieldObj.field.order} md={fieldObj.md} sm={12}>
                                {renderFormField(fieldObj.field)}
                            </Col>,
                        )
                    }
                    if (doubleSimpleField.length === 2) {
                        allFields.push(<Row>{doubleSimpleField}</Row>)
                        doubleSimpleField = []
                    }
                }
            })
        }
        return allFields
    }

    return (
        <StyledContainer>
            <div role="form">
                {props.registrationPagePreText && (
                    <StyledContrastText
                        as="div"
                        isCampaignPage={props.isCampaignPage}
                        className="registration-pre-text"
                        dangerouslySetInnerHTML={{ __html: props.registrationPagePreText }}
                    />
                )}
                <StyledContrastText
                    as="p"
                    tabIndex="0"
                    isCampaignPage={props.isCampaignPage}
                    isSingleColumnLayout={props.isSingleColumnLayout}
                >
                    Fields with an asterisk (*) are required.
                </StyledContrastText>
                <fieldset>
                    <ErrorFocusWrapper
                        syncErrors={syncErrors}
                        invalid={invalid}
                        submitFailed={submitFailed}
                        submitting={submitting}
                        externalSubmitButtonSelectors={['[data-cy="message-preview-submit-button"]', ".submit-button"]}
                    >
                        {renderAllFieldObjects()}
                    </ErrorFocusWrapper>
                </fieldset>
                {props.registrationPagePostText && (
                    <StyledContrastText
                        as="div"
                        isCampaignPage={props.isCampaignPage}
                        className="registration-post-text"
                        dangerouslySetInnerHTML={{ __html: props.registrationPagePostText }}
                        aria-live="polite"
                    />
                )}
            </div>
        </StyledContainer>
    )
}

UserInfoFormSection.propTypes = {
    form: PropTypes.string.isRequired,
    // user provided props, if using connected component
    registrationPageIds: PropTypes.arrayOf(PropTypes.number),
    additionalSimpleFields: PropTypes.arrayOf(PropTypes.number),
    showOnlyFilledCustomFields: PropTypes.bool,
    showOnlyUnfilledFields: PropTypes.bool,
    // derived props, found in UserInfoFormSection/selectors
    allSimpleAndCustomFieldsWidth: PropTypes.arrayOf(PropTypes.object).isRequired,
    customFieldOptionalFields: PropTypes.object,
    customFieldRequiredValues: PropTypes.object,
    optionalSimpleFields: PropTypes.object,
    requireAllFields: PropTypes.bool,
    registrationPagePreText: PropTypes.string,
    defaultRegistrationPage: PropTypes.object,
    uniqueWidgetId: PropTypes.string,
    campaign: PropTypes.object,
    organizationDesign: PropTypes.object,
    textingOrgName: PropTypes.string,
    textingMessageFrequency: PropTypes.number,
    textingTermsUrl: PropTypes.string,
    textingPrivacyUrl: PropTypes.string,
    t: PropTypes.func.isRequired,
    submitFailed: PropTypes.bool,
    invalid: PropTypes.bool,
    submitting: PropTypes.bool,
    syncErrors: PropTypes.object,
    // Accessibility props for binary toggle fields
    useAccessibleBinaryToggle: PropTypes.bool,
}

UserInfoFormSection.defaultProps = {
    requireAllFields: true,
    useAccessibleBinaryToggle: true,
}

const mapStateToProps = (state, ownProps) => {
    return {
        ...selectFormFields(state, ownProps),
        submitFailed: hasSubmitFailed(ownProps.form)(state),
        invalid: isInvalid(ownProps.form)(state),
        submitting: isSubmitting(ownProps.form)(state),
        syncErrors: getFormSyncErrors(ownProps.form)(state),
        // Ensure ownProps take precedence over selector results for accessibility props
        useAccessibleBinaryToggle: ownProps.useAccessibleBinaryToggle,
    }
}

export default connect(mapStateToProps)(UserInfoFormSection)
